/**
 * Orchestration de la reprise depuis la console (ARTO-132, `docs/dev/api.md`
 * § 12.18) : le plan qui lance les lots dans l'ordre des dépendances,
 * l'écartement des lignes refusées, l'annulation d'un lot et le scellement.
 *
 * Les types sont ceux de l'API ; les écrans n'en recalculent aucun compteur.
 * En mode `?demo`, aucune requête réseau (`demoPlan.ts`).
 */
import { ApiError, estErreurConfinement, modeDemo, requete } from './api.js';
import { signalerConfinement } from './confinement.js';
import { demoPlan } from './demoPlan.js';
import type { CompteursAnnulation, EtatLot, LotReprise, NatureLot } from './reprise.js';
import type { CompteursLot } from './reprise.js';

// ── Le plan ──────────────────────────────────────────────────────────────

export type EtatPlan = 'en_cours' | 'en_pause' | 'bloque' | 'termine' | 'abandonne';

/** Un plan qui occupe encore sa galerie : un seul à la fois, et aucune annulation. */
export function planActif(plan: Pick<PlanVue, 'etat'> | null | undefined): boolean {
  return plan?.etat === 'en_cours' || plan?.etat === 'en_pause' || plan?.etat === 'bloque';
}

/**
 * L'étape de chaque nature dans l'ordre des dépendances (§ 8.8). Le plan n'en
 * rend que les étapes où il a des lots ; l'écran montre les sept.
 */
export const ETAPE_DE_NATURE: Readonly<Record<NatureLot, number>> = {
  lieux: 1,
  artistes: 1,
  oeuvres: 2,
  images: 3,
  contacts: 4,
  expositions: 5,
  'factures-historiques': 6,
  documents: 7,
  localisations: 3,
  provenance: 3,
  editions: 3,
  membres: 8,
};

/** Les sept étapes des données, plus les membres, invités en dernier. */
export const NOMBRE_ETAPES = 8;

export const NATURES_PAR_ETAPE: ReadonlyArray<{ etape: number; natures: readonly NatureLot[] }> =
  Array.from({ length: NOMBRE_ETAPES }, (_, rang) => ({
    etape: rang + 1,
    natures: (Object.keys(ETAPE_DE_NATURE) as NatureLot[]).filter(
      (nature) => ETAPE_DE_NATURE[nature] === rang + 1,
    ),
  }));

export interface EtapePlan {
  etape: number;
  natures: NatureLot[];
  lots: number;
  termines: number;
  enCours: number;
  echoues: number;
}

export interface LotDuPlan {
  id: string;
  nature: NatureLot;
  etape: number;
  etat: EtatLot | null;
  morceau: { nature: NatureLot; rang: number } | null;
  compteurs: CompteursLot | null;
}

export interface ArretPlan {
  raison: string;
  lots: string[];
}

export interface PlanVue {
  id: string;
  galerieId: string;
  dossierId: string | null;
  etat: EtatPlan;
  revision: number;
  etape: number;
  etapes: EtapePlan[];
  lots: LotDuPlan[];
  arret: ArretPlan | null;
  operateur: string;
  travail: { id: string; etat: string; prochainPassage: string } | null;
  creeLe: string;
  termineLe: string | null;
}

/** Les gestes d'un plan en cours. */
export type GestePlan = 'pause' | 'reprendre' | 'abandonner';

const base = (idGalerie: string): string =>
  `/v1/console/galleries/${encodeURIComponent(idGalerie)}/reprise`;

/** Le plan actif de la galerie, ou le dernier ; `null` sans plan (404). */
export async function obtenirPlan(idGalerie: string): Promise<PlanVue | null> {
  if (modeDemo()) return demoPlan.plan(idGalerie);
  try {
    return await requete<PlanVue>(`${base(idGalerie)}/plan`);
  } catch (erreur) {
    if (erreur instanceof ApiError && erreur.status === 404) return null;
    throw erreur;
  }
}

/** `403 mfa_step_up_requis` si une facture antérieure ou un document confidentiel en est. */
export async function lancerPlan(idGalerie: string): Promise<PlanVue> {
  if (modeDemo()) return demoPlan.lancer(idGalerie);
  return requete<PlanVue>(`${base(idGalerie)}/plan`, { methode: 'POST', corps: {} });
}

export async function gestePlan(idGalerie: string, geste: GestePlan): Promise<PlanVue> {
  if (modeDemo()) return demoPlan.geste(idGalerie, geste);
  return requete<PlanVue>(`${base(idGalerie)}/plan/${geste}`, { methode: 'POST', corps: {} });
}

// ── Écartement ───────────────────────────────────────────────────────────

export const MOTIF_ECARTEMENT_MIN = 3;
export const MOTIF_ECARTEMENT_MAX = 500;

/** Écarte les lignes refusées à l'essai ; le lot passe `pret`. */
export async function ecarterLignes(
  idGalerie: string,
  lotId: string,
  motif: string,
): Promise<LotReprise> {
  if (modeDemo()) return demoPlan.ecarter(idGalerie, lotId, motif);
  return requete<LotReprise>(`${base(idGalerie)}/lots/${encodeURIComponent(lotId)}/ecarter`, {
    methode: 'POST',
    corps: { motif },
  });
}

/** Ce que le fichier des rejets d'un lot se demande : une feuille, et la nature d'un morceau. */
export interface CibleRejets {
  feuille: string;
  nature?: NatureLot;
}

/** Télécharge le fichier des rejets (CSV) : la session et le cookie, pas un simple lien. */
export async function telechargerRejets(
  idGalerie: string,
  lotId: string,
  cible: CibleRejets,
): Promise<void> {
  const nomFichier = `rejets-${cible.nature ?? 'lot'}-${cible.feuille}.csv`;
  let blob: Blob;
  if (modeDemo()) {
    blob = new Blob([demoPlan.rejets(idGalerie, lotId, cible)], {
      type: 'text/csv;charset=utf-8',
    });
  } else {
    const parametres = new URLSearchParams({ feuille: cible.feuille });
    if (cible.nature) parametres.set('nature', cible.nature);
    const reponse = await fetch(
      `${base(idGalerie)}/lots/${encodeURIComponent(lotId)}/rejets?${parametres.toString()}`,
      { credentials: 'include' },
    );
    if (!reponse.ok) {
      const erreur = (await reponse.json().catch(() => null)) as {
        code?: string;
        message?: string;
        details?: unknown;
      } | null;
      const refus = new ApiError(
        reponse.status,
        erreur?.code ?? 'erreur_inconnue',
        erreur?.message ?? 'Une erreur est survenue.',
        erreur?.details ?? null,
      );
      if (estErreurConfinement(refus)) signalerConfinement();
      throw refus;
    }
    blob = await reponse.blob();
  }
  const url = URL.createObjectURL(blob);
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nomFichier;
  document.body.append(lien);
  lien.click();
  lien.remove();
  URL.revokeObjectURL(url);
}

// ── Annulation et scellement ─────────────────────────────────────────────

/** Une fiche citée par le rapport d'annulation : jamais une ligne du fichier. */
export interface FicheCitee {
  modele: string;
  id: string;
}

export interface FicheGardee extends FicheCitee {
  /** `modifie`, `etat_inconnu`, `reference:<motif>`. */
  motifs: string[];
  /** Sa ligne dans le fichier du lot (le pivot, pour un morceau), d'après son reçu. */
  ligne?: number;
  /** Sa référence d'origine qualifiée, `source:id`, quand la fiche en porte une. */
  refOrigine?: string;
}

/** Le rapport de la dernière annulation faite (`GET …/annulation`). */
export interface RapportAnnulation {
  version: number;
  lotId: string;
  importId: string;
  nature: NatureLot;
  travailId: string;
  etabliLe: string;
  supprimes: FicheCitee[];
  conserves: FicheGardee[];
  dejaAbsents: FicheCitee[];
  /** Complétions, jamais défaites. */
  nonDefaites: number;
  /** Créations sans cible connue (anciens reçus). */
  sansCible: number;
  /** Originaux, variantes et pièces retirés du stockage. */
  objetsRetires: number;
}

export type { CompteursAnnulation };

/** Direction de Venio, second facteur récent : `403 mfa_step_up_requis` sinon. */
export async function annulerLot(idGalerie: string, lotId: string): Promise<LotReprise> {
  if (modeDemo()) return demoPlan.annuler(idGalerie, lotId);
  return requete<LotReprise>(`${base(idGalerie)}/lots/${encodeURIComponent(lotId)}/annuler`, {
    methode: 'POST',
    corps: {},
  });
}

export async function lireAnnulation(idGalerie: string, lotId: string): Promise<RapportAnnulation> {
  if (modeDemo()) return demoPlan.rapportAnnulation(idGalerie, lotId);
  return requete<RapportAnnulation>(
    `${base(idGalerie)}/lots/${encodeURIComponent(lotId)}/annulation`,
  );
}

export interface ResultatScellement {
  scelles: number;
  scelleLe: string;
  dossierId: string | null;
}

/** Direction de Venio, second facteur récent : la reprise ne s'annule plus. */
export async function sceller(idGalerie: string): Promise<ResultatScellement> {
  if (modeDemo()) return demoPlan.sceller(idGalerie);
  return requete<ResultatScellement>(`${base(idGalerie)}/scellement`, {
    methode: 'POST',
    corps: {},
  });
}

// ── Références d'origine ─────────────────────────────────────────────────

/**
 * Une référence d'origine rangée qualifiée, `<source>:<identifiant>`
 * (`manuel:1042`). Une référence sans source (ancienne) rend `source: null`.
 */
export function separerReference(reference: string): { source: string | null; id: string } {
  const rang = reference.indexOf(':');
  if (rang <= 0) return { source: null, id: reference };
  return { source: reference.slice(0, rang), id: reference.slice(rang + 1) };
}

/**
 * Le refus « collision entre sources » : une fiche reconnue autrement que par
 * sa référence porte celle d'une AUTRE source. Rend la référence citée par le
 * message de l'API, ou `null` si le message est autre chose.
 */
const MOTIF_COLLISION = /déjà repris depuis une autre source\s*\(«\s*([^»]+?)\s*»\)/;

export function collisionDeSource(message: string): string | null {
  return MOTIF_COLLISION.exec(message)?.[1] ?? null;
}

/** Le message du delta : la fiche a changé depuis sa reprise, rien n'a été écrasé. */
export function estMessageDelta(message: string): boolean {
  return /Rien n’est écrasé : à revoir/.test(message);
}

// ── Erreurs nommées ──────────────────────────────────────────────────────

/** Les raisons de l'API que les écrans du plan, de l'écartement et de l'annulation disent en clair. */
export const RAISONS_PLAN = [
  'rien_a_lancer',
  'plan_en_cours',
  'plan_absent',
  'plan_modifie',
  'plan_actif',
  'annulation_en_cours',
  'reprise_en_cours',
  'rien_a_ecarter',
  'lot_non_essaye',
  'essai_a_refaire',
  'ecartement_perime',
  'lot_scelle',
  'lot_annule',
  'lot_non_execute',
  'galerie_en_service',
  'lot_meme_nature_en_cours',
] as const;
export type RaisonPlan = (typeof RAISONS_PLAN)[number];

export function raisonPlan(erreur: unknown): RaisonPlan | null {
  if (!(erreur instanceof ApiError)) return null;
  const details = erreur.details as { raison?: unknown } | null;
  for (const candidate of [details?.raison, erreur.code]) {
    const trouvee = RAISONS_PLAN.find((raison) => raison === candidate);
    if (trouvee) return trouvee;
  }
  return null;
}

/**
 * L'ordre dans lequel on annule les morceaux d'un lot : à l'envers de celui
 * du plan (étape, nature par ordre alphabétique, rang du morceau).
 */
export function ordreInverseDuPlan<T extends { nature: NatureLot; rang: number }>(
  morceaux: readonly T[],
): T[] {
  return [...morceaux].sort(
    (a, b) =>
      ETAPE_DE_NATURE[b.nature] - ETAPE_DE_NATURE[a.nature] ||
      b.nature.localeCompare(a.nature) ||
      b.rang - a.rang,
  );
}
